import { Option, SelectedItems } from "@/types/productTypes";
import { Box, Heading, useRadioGroup, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { ControlHover } from "../MainEditorPanel";
import ItemVisualizer from "./ItemVisualizer";

type OptionVisualizerProps = {
   option: Option | null;
   controlHovered: ControlHover | null;
   setControlHovered: React.Dispatch<React.SetStateAction<ControlHover | null>>;
   stageId: number;
};

export default function OptionVisualizer({
   option,
   controlHovered,
   setControlHovered,
   stageId,
}: OptionVisualizerProps) {
   const [value, setValue] = useState<SelectedItems | null>(null);

   const { getRootProps, getRadioProps } = useRadioGroup({
      value: value?.selectedItem as string,
      onChange: (value) => {
         setValue({ selectedItem: value, selected: true });
      },
   });
   if (!option) {
      return null;
   }
   const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!option.id) return;
      if (e.ctrlKey) {
         setControlHovered({
            type: "Option",
            stageId: stageId,
            optionId: option.id,
            itemId: null,
         });
      }
   };

   const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!option.id) return;
      e.stopPropagation();
      // Update hover state dynamically while the mouse moves within the element
      if (e.ctrlKey) {
         setControlHovered({
            type: "Option",
            stageId: stageId,
            optionId: option.id,
            itemId: null,
         });
      } else {
         setControlHovered(null);
      }
   };

   const handleMouseLeave = () => {
      setControlHovered(null);
   };

   return (
      <div
         onMouseEnter={handleMouseEnter}
         onMouseMove={handleMouseMove}
         onMouseLeave={handleMouseLeave}
         className={`w-full flex flex-row  ${
            controlHovered?.optionId === option.id &&
            controlHovered?.type === "Option"
               ? "border-2 border-blue-500 border-dashed"
               : "border-2 border-white"
         } `}
      >
         <RadioOption
            stageId={stageId}
            option={option}
            controlValue={{ selectedItem: "", selected: false }}
            getRadioProps={getRadioProps}
            getRootProps={getRootProps}
            controlHovered={controlHovered}
            setControlHovered={setControlHovered}
         />
      </div>
   );
}
type RadioOptionUiProps = {
   stageId: number;
   option: Option;
   getRootProps?: any;
   getRadioProps?: (options: { value: string }) => any;
   controlValue: SelectedItems;
   controlHovered: ControlHover | null;
   setControlHovered: React.Dispatch<React.SetStateAction<ControlHover | null>>;
};

function RadioOption({
   stageId,
   option,
   getRootProps,
   getRadioProps,
   controlValue,
   controlHovered,
   setControlHovered,
}: RadioOptionUiProps) {
   return (
      <Box mb="1.5rem">
         <Heading
            as="h3"
            fontSize="1rem"
            fontWeight="600"
            textTransform="uppercase"
            color="gray.700"
            mb="0.5rem"
         >
            {option.name}
         </Heading>
         <VStack align="stretch" spacing={4} {...getRootProps()}>
            {option.items?.map((item, index) => {
               if (!option.id) return;
               const radio =
                  getRadioProps && getRadioProps({ value: item.idName });
               return (
                  <ItemVisualizer
                     optionId={option.id}
                     stageId={stageId}
                     key={index}
                     item={item}
                     index={index}
                     controlValue={controlValue}
                     radio={radio}
                     controlHovered={controlHovered}
                     setControlHovered={setControlHovered}
                  />
               );
            })}
         </VStack>
      </Box>
   );
}
